import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, MessageSquareText, Copy, Check, X, ShieldCheck, LogOut, Download, Link2 } from "lucide-react";
import { api, vibrate, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GoogleButton from "@/components/GoogleButton";
import MagicLink from "@/components/MagicLink";
import { PLATFORMS, STATUS_META, inviteLink, buildDM } from "@/lib/prospect";

const pct = (a, b) => (b ? Math.min(100, Math.round((a / b) * 100)) : 0);

function GoalBar({ label, value, target, color }) {
  const done = target && value >= target;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-zinc-400">{label}</span>
        <span className={`font-semibold ${done ? "text-emerald-400" : "text-zinc-200"}`}>{value}/{target} {done && "✓"}</span>
      </div>
      <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct(value, target)}%`, background: done ? "#34d399" : color }} />
      </div>
    </div>
  );
}

function ScriptsModal({ prospect, onClose }) {
  const [tone, setTone] = useState("cash");
  const [copied, setCopied] = useState(false);
  const text = buildDM(prospect, tone);
  const copy = async () => { try { await navigator.clipboard.writeText(text); } catch {} vibrate(); setCopied(true); toast.success("Copié ✓"); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="pro-scripts-modal" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <MessageSquareText size={18} className="text-pink-400" />
          <h3 className="font-bold">Script DM — {prospect.name || prospect.handle}</h3>
          <button onClick={onClose} className="ml-auto text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="flex gap-2 mb-3">
          {[["cash", "Cash / jeune"], ["pro", "Pro / premium"]].map(([k, l]) => (
            <button key={k} data-testid={`pro-scripts-tone-${k}`} onClick={() => setTone(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${tone === k ? "bg-pink-500/15 border-pink-500/40 text-pink-200" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}>{l}</button>
          ))}
        </div>
        <div className="relative">
          <textarea readOnly value={text} data-testid="pro-scripts-dm" className="w-full h-44 rounded-xl bg-zinc-950 border border-zinc-800 p-3 text-sm text-zinc-200 resize-none outline-none" />
          <button onClick={copy} className="absolute top-2 right-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs font-semibold flex items-center gap-1">
            {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />} Copier
          </button>
        </div>
      </div>
    </div>
  );
}

const euro = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

function CommissionCard({ c }) {
  if (!c) return null;
  const rate = Math.round((c.override_rate || 0.1) * 100);
  const creators = c.creators || [];
  return (
    <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border border-emerald-500/25 p-4 mb-4" data-testid="commercial-commission">
      <p className="text-sm font-bold mb-3">💰 Tes gains</p>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl bg-zinc-950/60 border border-zinc-800 p-3">
          <p className="text-[11px] uppercase tracking-wide text-zinc-500">À recevoir</p>
          <p className="text-2xl font-black text-emerald-400" data-testid="commission-pending">{euro(c.pending)}</p>
          {c.paid_out > 0 && <p className="text-[11px] text-zinc-500 mt-0.5">déjà versé : {euro(c.paid_out)}</p>}
        </div>
        <div className="rounded-xl bg-zinc-950/60 border border-zinc-800 p-3">
          <p className="text-[11px] uppercase tracking-wide text-zinc-500">Récurrent / mois</p>
          <p className="text-2xl font-black text-zinc-100" data-testid="commission-monthly">{euro(c.override_monthly)}</p>
          <p className="text-[11px] text-zinc-500 mt-0.5">{rate}% du CA de tes créateurs</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-[11px] mb-3">
        <span className="rounded-full bg-zinc-800/80 px-2.5 py-1 text-zinc-300">{c.creators_recruited} créateur·s recruté·s</span>
        <span className="rounded-full bg-zinc-800/80 px-2.5 py-1 text-zinc-300">{c.paying_total} abonné·s payant·s</span>
        <span className="rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2.5 py-1 text-emerald-300">{c.bonus_count} prime·s × {euro(c.milestone_bonus)}</span>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">
        Tu touches <b className="text-zinc-300">{rate}% à vie</b> du CA des abonnés ramenés par tes créateurs,
        + une <b className="text-zinc-300">prime de {euro(c.milestone_bonus)}</b> dès qu'un créateur atteint {c.milestone_sales} abonnés payants.
      </p>

      {creators.length > 0 ? (
        <div data-testid="commission-creators">
          <p className="text-xs font-bold text-zinc-400 mb-2">📊 Qui vend ({creators.length})</p>
          <div className="space-y-2">
            {creators.map((cr) => {
              const p = pct(cr.paying, cr.milestone_target);
              return (
                <div key={cr.code} className="rounded-xl bg-zinc-950/60 border border-zinc-800 p-3" data-testid={`commission-creator-${cr.code}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-sm font-semibold text-zinc-100 truncate">{cr.name}</span>
                    <span className="text-xs text-emerald-400 font-bold shrink-0">{euro(cr.override_monthly)}/mois</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-zinc-500 mb-1">
                    <span>{cr.paying} abonné·s payant·s · {euro(cr.mrr)} MRR</span>
                    <span className={cr.milestone_reached ? "text-emerald-400 font-semibold" : ""}>
                      {cr.milestone_reached ? `🏆 Prime ${euro(c.milestone_bonus)} atteinte` : `${cr.paying}/${cr.milestone_target} vers la prime`}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, background: cr.milestone_reached ? "#34d399" : "#ec4899" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-zinc-500 rounded-xl bg-zinc-950/60 border border-zinc-800 p-3">
          Aucun créateur recruté pour l'instant. Partage ton lien perso 👆 — chaque créateur qui s'inscrit et vend te rapporte.
        </p>
      )}
    </div>
  );
}

function CommercialDashboard({ data, reload }) {
  const [form, setForm] = useState({ name: "", platform: "TikTok", handle: "", email: "", followers: "", niche: "" });
  const [busy, setBusy] = useState(false);
  const [scriptsFor, setScriptsFor] = useState(null);
  const [copied, setCopied] = useState(null);
  const [invitingId, setInvitingId] = useState(null);
  const g = data.goal;

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim() && !form.handle.trim()) { toast.error("Nom ou pseudo requis"); return; }
    setBusy(true); vibrate();
    try { await api.post("/commercial/prospects", form); toast.success("Prospect ajouté"); setForm({ name: "", platform: "TikTok", handle: "", email: "", followers: "", niche: "" }); reload(); }
    catch { toast.error("Échec"); } finally { setBusy(false); }
  };
  const invite = async (p, tone) => {
    setInvitingId(p.prospect_id); vibrate();
    try { const r = await api.post(`/commercial/prospects/${p.prospect_id}/invite`, { tone }); toast.success(r.data.email_sent ? "Email envoyé 📧" : "Échec"); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Échec"); } finally { setInvitingId(null); }
  };
  const setStatus = async (p, status) => { try { await api.patch(`/commercial/prospects/${p.prospect_id}`, { status }); reload(); } catch { toast.error("Échec"); } };
  const copyLink = async (p) => { try { await navigator.clipboard.writeText(inviteLink(p.code)); } catch {} vibrate(); setCopied(p.prospect_id); toast.success("Lien copié"); setTimeout(() => setCopied(null), 1500); };
  const [linkCopied, setLinkCopied] = useState(false);
  const copyMyLink = async () => { try { await navigator.clipboard.writeText(data.invite_link); } catch {} vibrate(); setLinkCopied(true); toast.success("Ton lien est copié 🎉"); setTimeout(() => setLinkCopied(false), 1500); };

  return (
    <div className="max-w-3xl mx-auto px-5 py-6" data-testid="commercial-dashboard">
      {scriptsFor && <ScriptsModal prospect={scriptsFor} onClose={() => setScriptsFor(null)} />}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-pink-400 font-bold">Espace Commercial</p>
          <h1 className="font-display text-2xl font-black">Salut {(data.name || "").split(" ")[0]} 👋</h1>
        </div>
      </div>

      {/* Personalized link + kit */}
      <div className="rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/25 p-4 mb-4" data-testid="commercial-link-card">
        <p className="text-sm font-bold mb-1">🔗 Ton lien personnel</p>
        <p className="text-xs text-zinc-400 mb-3">Chaque personne qui s'inscrit via ce lien est comptée dans TES résultats.</p>
        <div className="flex items-center gap-2 rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5">
          <Link2 size={15} className="text-pink-400 shrink-0" />
          <span className="text-xs text-zinc-300 truncate flex-1" data-testid="commercial-invite-link">{data.invite_link}</span>
          <button data-testid="commercial-copy-link" onClick={copyMyLink} className="rounded-lg bg-pink-500 hover:bg-pink-600 text-white text-xs font-bold px-3 py-1.5 flex items-center gap-1 shrink-0">
            {linkCopied ? <Check size={13} /> : <Copy size={13} />} Copier
          </button>
        </div>
        <a href={`${API}/creators/kit.pdf?com=${data.code}`} target="_blank" rel="noreferrer" data-testid="commercial-kit-pdf"
          className="mt-2.5 flex items-center justify-center gap-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold py-2.5">
          <Download size={15} /> Télécharger mon kit PDF (à envoyer)
        </a>
      </div>

      {/* Goals */}
      <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4 mb-4" data-testid="commercial-goals">
        <p className="text-sm font-bold mb-3">🎯 Tes objectifs de la semaine</p>
        <div className="space-y-3">
          <GoalBar label="Prospects contactés" value={g.contacted_week} target={g.target_contacted} color="#38bdf8" />
          <GoalBar label="Créateurs inscrits" value={g.joined_week} target={g.target_joined} color="#ec4899" />
        </div>
      </div>

      {/* Commission / earnings */}
      <CommissionCard c={data.commission} />

      {/* Add */}
      <form onSubmit={add} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4 space-y-3 mb-4" data-testid="commercial-add-prospect">
        <p className="text-sm font-bold">Ajouter un prospect</p>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="com-prospect-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom / pseudo" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
          <select data-testid="com-prospect-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500">
            {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input data-testid="com-prospect-handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="@pseudo" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
          <input data-testid="com-prospect-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (pour l'invitation)" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
        </div>
        <button data-testid="com-prospect-submit" disabled={busy} className="w-full rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-2.5 text-sm disabled:opacity-60">{busy ? "…" : "Ajouter"}</button>
      </form>

      {/* List */}
      <div className="space-y-2" data-testid="commercial-prospects">
        {(data.prospects || []).map((p) => {
          const sm = STATUS_META[p.status] || STATUS_META.todo;
          return (
            <div key={p.prospect_id} className="rounded-xl bg-zinc-900 border border-zinc-800 p-3" data-testid={`com-prospect-${p.code}`}>
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{p.name || p.handle}</p>
                  <p className="text-xs text-zinc-500 truncate">{p.platform || "—"}{p.email ? ` · ${p.email}` : " · pas d'email"}</p>
                </div>
                <span className={`text-[10px] font-bold rounded-full border px-2 py-0.5 ${sm.c}`}>{sm.label}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <button data-testid={`com-email-cash-${p.code}`} onClick={() => invite(p, "cash")} disabled={!p.email || invitingId === p.prospect_id}
                  className="rounded-full px-2.5 py-1 text-xs font-semibold border border-pink-500/30 text-pink-300 hover:bg-pink-500/10 flex items-center gap-1 disabled:opacity-40">
                  {invitingId === p.prospect_id ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Email cash
                </button>
                <button data-testid={`com-email-pro-${p.code}`} onClick={() => invite(p, "pro")} disabled={!p.email || invitingId === p.prospect_id}
                  className="rounded-full px-2.5 py-1 text-xs font-semibold border border-sky-500/30 text-sky-300 hover:bg-sky-500/10 flex items-center gap-1 disabled:opacity-40">
                  <Mail size={11} /> Email pro
                </button>
                <button data-testid={`com-scripts-${p.code}`} onClick={() => setScriptsFor(p)} className="rounded-full px-2.5 py-1 text-xs font-semibold border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex items-center gap-1">
                  <MessageSquareText size={11} /> Scripts DM
                </button>
                <button data-testid={`com-copy-${p.code}`} onClick={() => copyLink(p)} className="rounded-full px-2.5 py-1 text-xs font-semibold border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex items-center gap-1">
                  {copied === p.prospect_id ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />} Lien
                </button>
                <select data-testid={`com-status-${p.code}`} value={p.status} onChange={(e) => setStatus(p, e.target.value)} className="rounded-full bg-zinc-950 border border-zinc-700 text-xs text-zinc-300 px-2 py-1 outline-none">
                  <option value="todo">À contacter</option>
                  <option value="contacted">Contacté</option>
                  <option value="joined">Inscrit</option>
                </select>
              </div>
            </div>
          );
        })}
        {!(data.prospects || []).length && <p className="text-sm text-zinc-500 text-center py-10">Aucun prospect encore. Ajoute ton premier créateur à contacter 👆</p>}
      </div>
    </div>
  );
}

export default function Pro() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(true);

  const load = () => api.get("/commercial/me").then((r) => setData(r.data)).catch(() => setData({ is_commercial: false })).finally(() => setChecking(false));

  useEffect(() => {
    if (loading) return;
    if (!user) { localStorage.setItem("dmd_pro_intent", "1"); setChecking(false); return; }
    localStorage.removeItem("dmd_pro_intent");
    load();
    /* eslint-disable-next-line */
  }, [user, loading]);

  if (loading || (user && checking)) {
    return <div className="min-h-screen bg-[#09090b] flex items-center justify-center"><Loader2 className="animate-spin text-pink-500" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center px-6">
        <div className="w-full max-w-sm text-center" data-testid="pro-login">
          <div className="text-5xl mb-3">🧑‍💼</div>
          <p className="text-xs uppercase tracking-[0.25em] text-pink-400 font-bold">Espace Commercial</p>
          <h1 className="font-display text-2xl font-black mt-2">Connecte-toi à ton espace</h1>
          <p className="text-sm text-zinc-400 mt-2 mb-5">Utilise l'email fourni par l'admin pour accéder à tes prospects et objectifs.</p>
          <div className="space-y-3">
            <GoogleButton testId="pro-google-button" label="Continuer avec Google" />
            <div className="flex items-center gap-3"><div className="flex-1 h-px bg-zinc-800" /><span className="text-[11px] text-zinc-600 uppercase">ou</span><div className="flex-1 h-px bg-zinc-800" /></div>
            <MagicLink />
            <p className="flex items-center justify-center gap-1.5 text-xs text-zinc-500"><ShieldCheck size={13} className="text-emerald-400" /> Connexion sans mot de passe.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data?.is_commercial) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center px-6">
        <div className="text-center max-w-sm" data-testid="pro-not-commercial">
          <div className="text-5xl mb-3">🚫</div>
          <h1 className="font-display text-2xl font-black">Accès réservé</h1>
          <p className="text-sm text-zinc-400 mt-2">Le compte <b className="text-zinc-200">{user.email}</b> n'est pas enregistré comme commercial·e. Contacte l'admin pour être ajouté·e.</p>
          <a href="/app" className="mt-5 inline-flex items-center gap-1.5 text-sm text-pink-400 hover:text-pink-300"><LogOut size={15} /> Retour à l'app</a>
        </div>
      </div>
    );
  }

  return <div className="min-h-screen bg-[#09090b] text-white"><CommercialDashboard data={data} reload={load} /></div>;
}
